import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Space, Row, Col, Spin, message } from 'antd';
import get from 'lodash/get';
import assign from 'lodash/assign';
import { useSelector, useDispatch } from 'react-redux';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { userMangement } from '../../redux/actions/authActions';
import {
  COMPANY_UPDATE_SUCCESS,
  COMPANY_ADDED_SUCCESS,
} from '../../constants/messages';
import ImageUpload from '../imageUpload';
import { hasPermission } from '../../utilities/helpers';

function CompanyDetailForm(props) {
  const { refreshUser, edit, toggleLoginModal } = props;
  const { permissions } = useSelector((state) => get(state, 'auth.role'));

  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const selectedCompany = useSelector((state) =>
    get(state, 'auth.selectedCompany')
  );
  const [selectedImage, setSelectedImage] = useState(
    edit ? get(selectedCompany, 'logo') : undefined
  );
  const dispatch = useDispatch();
  const onFinish = (values) => {
    setLoading(true);
    const gstArr = get(values, 'gst.0') ? get(values, 'gst') : [];
    const fssaiArr = get(values, 'fssai_license.0')
      ? get(values, 'fssai_license')
      : [];
    if (selectedImage) {
      assign(values, { logo: selectedImage });
    }

    if (edit) {
      dispatch(
        userMangement(
          'update.company',
          assign(values, {
            company_id: get(selectedCompany, 'id'),
            fssai_license: fssaiArr,
            gst: gstArr,
            logo: selectedImage || undefined,
          })
        )
      )
        .then(() => {
          message.success(COMPANY_UPDATE_SUCCESS);
          refreshUser();
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      dispatch(
        userMangement(
          'create.company',
          assign(values, {
            parent_company_id: get(selectedCompany, 'id'),
            fssai_license: fssaiArr,
            gst: gstArr,
          })
        )
      )
        .then(() => {
          message.success(COMPANY_ADDED_SUCCESS);
          refreshUser();
          setLoading(false);
          toggleLoginModal();
        })
        .catch(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    if (edit) {
      form.setFieldsValue(
        assign(selectedCompany, {
          gst: get(selectedCompany, 'gst', []).length
            ? get(selectedCompany, 'gst', [])
            : [undefined],
          fssai_license: get(selectedCompany, 'fssai_license', []).length
            ? get(selectedCompany, 'fssai_license', [])
            : [undefined],
        })
      );
      setSelectedImage(get(selectedCompany, 'logo'));
    }
  }, [selectedCompany]);

  const hasEditPermission = hasPermission(permissions, ['PERM_UPDATE_COMPANY']);

  return (
    <div>
      <Spin spinning={loading}>
        <Form
          name="basic"
          form={form}
          layout="vertical"
          initialValues={{ gst: [undefined], fssai_license: [undefined] }}
          onFinish={onFinish}
          autoComplete="off"
        >
          <Row gutter={16} justify="space-between">
            <Col xl={8}>
              <div className="mt-20">
                <Form.Item
                  label="Name of Company Name"
                  name="name"
                  className="form-item"
                  rules={[
                    {
                      required: true,
                      message: 'Please input your company name!',
                    },
                  ]}
                >
                  <Input size="large" placeholder="Enter company name" />
                </Form.Item>
              </div>

              <div className="mt-20">
                <Form.Item
                  label="Type of Product "
                  name="product_type"
                  className="form-item"
                  rules={[
                    {
                      required: true,
                      message: 'Please input product type you deal in!',
                    },
                    {
                      min: 2,
                      message: 'Product Type cannot be less than 2 characters!',
                    },
                    {
                      max: 50,
                      message:
                        'Product Type cannot be more than 50 characters!',
                    },
                  ]}
                >
                  <Input size="large" placeholder="Enter type of product" />
                </Form.Item>
              </div>

              <div className="mt-20">
                <Form.Item
                  label="PAN Number of company"
                  name="pan"
                  className="form-item"
                  rules={[
                    {
                      required: true,
                      message: 'Please input your PAN number!',
                    },
                    {
                      min: 10,
                      message: 'Min Length of PAN is 10!',
                    },
                    {
                      max: 10,
                      message: 'Max length of PAN is 10!',
                    },
                  ]}
                >
                  <Input size="large" placeholder="Enter PAN number" />
                </Form.Item>
              </div>

              <div className="mt-40">
                <Form.List name="fssai_license">
                  {(fields, { add, remove: removeInput }) => (
                    <>
                      {fields.map(({ name, key }, index) => (
                        <Form.Item
                          key={key}
                          name={[name]}
                          label={index === 0 ? 'FSSAI LICENSE' : ''}
                          rules={[
                            {
                              min: 3,
                              message:
                                'FSSAI License cannot be less than 3 characters',
                            },
                          ]}
                        >
                          <Input
                            placeholder="FSSAI licence"
                            size="large"
                            addonAfter={
                              index === fields.length - 1 ? (
                                <PlusOutlined onClick={() => add()} />
                              ) : (
                                <MinusOutlined
                                  onClick={() => removeInput(name)}
                                />
                              )
                            }
                          />
                        </Form.Item>
                      ))}
                    </>
                  )}
                </Form.List>
              </div>
              <div className="mt-40">
                <Form.List name="gst">
                  {(fields, { add, remove: removeInput }) => (
                    <>
                      {fields.map(({ name, key }, index) => (
                        <Form.Item
                          key={key}
                          label={index === 0 ? 'GST Number' : ''}
                          name={[name]}
                          rules={[
                            {
                              min: 3,
                              message:
                                'GST Number cannot be less than 3 characters',
                            },
                          ]}
                        >
                          <Input
                            placeholder="GST No."
                            size="large"
                            addonAfter={
                              index === fields.length - 1 ? (
                                <PlusOutlined onClick={() => add()} />
                              ) : (
                                <MinusOutlined
                                  onClick={() => removeInput(name)}
                                />
                              )
                            }
                          />
                        </Form.Item>
                      ))}
                    </>
                  )}
                </Form.List>
              </div>

              {hasEditPermission && (
                <div className="mt-40">
                  <Space size="large">
                    <Button
                      type="primary"
                      htmlType="submit"
                      className="login-btn"
                    >
                      Save
                    </Button>
                  </Space>
                </div>
              )}
            </Col>
            <Col xl={4} />
            <Col xl={8}>
              <div className="mt-20">
                <ImageUpload
                  loading={loading}
                  setLoading={setLoading}
                  selectedImage={selectedImage}
                  setSelectedImage={setSelectedImage}
                />
              </div>
              <div className="mt-20">
                <Form.Item
                  label="Address of company with Pin Code"
                  name="address"
                  className="form-item"
                  rules={[
                    {
                      required: true,
                      message: 'Please input your address!',
                    },
                  ]}
                >
                  <Input.TextArea
                    size="large"
                    placeholder="Enter address with pincode"
                  />
                </Form.Item>
              </div>
            </Col>
          </Row>
        </Form>
      </Spin>
    </div>
  );
}

export default CompanyDetailForm;
